import type { UIMerchantProduct } from './dataTransformers'
import type { MerchantProductCardProps } from '../components/listing/mobile/MerchantProductCard'

/**
 * Derive quality status from product data
 */
const deriveQualityFromProduct = (product: UIMerchantProduct): "good" | "incomplete" | "poor" => {
  let score = 0
  
  // Check image availability
  if (product.image) score += 1
  
  // Check basic required fields (name, brand, sku)
  if (product.name && product.brand && product.sku) score += 1
  
  // Check stock levels
  if (product.stock.quantity > 10) {
    score += 1
  } else if (product.stock.quantity > 0) {
    score += 0.5
  }
  
  // Check if product has complete pricing info
  if (product.price.current > 0) score += 1
  
  // Check if product has variants (complexity bonus)
  if (product.variants && product.variants.count > 0) score += 0.5
  
  // Score interpretation
  if (score >= 4) return "good"
  if (score >= 2) return "incomplete"
  return "poor"
}

/**
 * Get stock status based on quantity
 */
const getStockStatus = (quantity: number): "in-stock" | "low-stock" | "out-of-stock" => {
  if (quantity === 0) return "out-of-stock"
  if (quantity <= 10) return "low-stock"
  return "in-stock"
}

/**
 * Transform UIMerchantProduct to MerchantProductCardProps
 * Note: Image selection is already handled in transformApiToUIProduct
 */
export const transformMerchantProductToCard = (
  product: UIMerchantProduct,
  onAction: (action: string, productId: string) => void
): MerchantProductCardProps => {
  return {
    id: product.id,
    name: product.name,
    sku: product.sku || 'No SKU',
    brand: product.brand || 'Unknown Brand',
    image: product.image, // Already contains best available image
    price: {
      current: product.price.current,
      range: product.variants?.priceRange,
      currency: 'IDR'
    },
    stock: {
      quantity: product.stock.quantity,
      status: getStockStatus(product.stock.quantity)
    },
    sales: {
      quantity30d: product.sales.quantity30d,
      revenue30d: product.sales.revenue30d
    },
    variants: product.variants ? {
      count: product.variants.count,
      priceRange: product.variants.priceRange
    } : undefined,
    quality: deriveQualityFromProduct(product),
    onAction
  }
}